import { useState } from "react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { ArrowUpRight, Heart, Building2, Sparkles, Award, Users } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { usePageSEO } from "@/lib/seo";

const opties = [
  {
    icon: Heart,
    label: "Doneren",
    body: "Als particulier bijdragen aan onze programma's — eenmalig of periodiek.",
  },
  {
    icon: Building2,
    label: "Bedrijfspartner",
    body: "Als bedrijf structureel bijdragen aan een programma of specifiek project.",
  },
  {
    icon: Sparkles,
    label: "Project financieren",
    body: "Een specifiek project geheel of gedeeltelijk mogelijk maken.",
  },
  {
    icon: Award,
    label: "Fonds of sponsor",
    body: "Als fonds bijdragen aan wendbare pilots binnen één van onze programma's.",
  },
  {
    icon: Users,
    label: "Expertise inbrengen",
    body: "Uw kennis of netwerk beschikbaar stellen — pro bono of tegen gereduceerd tarief.",
  },
];

export default function SteunPage() {
  usePageSEO({
    title: "Steun FIN | Stichting FIN",
    description:
      "Help perspectief mogelijk maken. Doneren, bedrijfspartner worden, een project financieren of expertise inbrengen — kies wat bij u past.",
  });

  const [form, setForm] = useState({
    naam: "",
    email: "",
    telefoon: "",
    organisatie: "",
    interesse: "",
    bericht: "",
  });
  const [loading, setLoading] = useState(false);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact", {
        type: "steun",
        naam: form.naam,
        email: form.email,
        telefoon: form.telefoon || undefined,
        organisatie: form.organisatie || undefined,
        rol: form.interesse || undefined,
        bericht: form.bericht,
      });
      toast.success("Bedankt — we nemen zo snel mogelijk contact met u op.");
      setForm({ naam: "", email: "", telefoon: "", organisatie: "", interesse: "", bericht: "" });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="steun-page">
      {/* Hero */}
      <section className="relative bg-petrol text-cream overflow-hidden">
        <div aria-hidden="true" className="absolute inset-0 opacity-30">
          <img
            src="https://images.unsplash.com/photo-1591343395082-e120087004b4?auto=format&fit=crop&w=2000&q=80"
            alt=""
            className="w-full h-full object-cover"
            loading="eager"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-petrol via-petrol/95 to-petrol/60" />
        </div>
        <div className="container-x pt-20 md:pt-24 pb-14 max-w-4xl relative">
          <p className="text-[11px] uppercase tracking-[0.28em] font-bold text-gold mb-5">Steun FIN</p>
          <h1 className="font-display text-[clamp(2.4rem,6vw,4.6rem)] leading-[1.02] tracking-tight text-balance">
            Help perspectief <em className="italic text-gold">mogelijk maken</em>.
          </h1>
          <p className="mt-6 text-lg leading-relaxed text-cream/85 text-pretty">
            Elk gebaar telt. Of u nu wilt doneren, samenwerken of expertise wilt inbrengen — we horen graag hoe u kunt en
            wilt bijdragen. Uw steun maakt het verschil voor mensen en gemeenschappen die vooruit willen.
          </p>
        </div>
      </section>

      {/* Opties */}
      <section className="py-20 md:py-24 bg-cream">
        <div className="container-x">
          <div className="max-w-3xl mb-12">
            <p className="text-[11px] uppercase tracking-[0.28em] font-bold text-gold mb-4">Manieren om bij te dragen</p>
            <h2 className="font-display text-4xl md:text-5xl leading-tight tracking-tight text-balance text-petrol">
              Vijf manieren. <em className="italic text-gold">Uw keuze.</em>
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {opties.map((o) => {
              const Icon = o.icon;
              return (
                <article
                  key={o.label}
                  className="bg-sandSoft border border-line rounded-3xl p-7 hover:shadow-card transition"
                  data-testid={`steun-optie-${o.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <span className="inline-flex items-center justify-center h-11 w-11 rounded-xl bg-goldSoft text-petrol mb-4">
                    <Icon size={22} strokeWidth={1.75} />
                  </span>
                  <h3 className="font-display text-2xl text-petrol leading-tight mb-2">{o.label}</h3>
                  <p className="text-inkMuted leading-relaxed text-[15px] text-pretty">{o.body}</p>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* Formulier */}
      <section className="py-20 md:py-24 bg-sand">
        <div className="container-x grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <p className="text-[11px] uppercase tracking-[0.28em] font-bold text-gold mb-4">Laat het ons weten</p>
            <h2 className="font-display text-4xl md:text-5xl leading-tight tracking-tight text-balance text-petrol mb-5">
              Vertel ons hoe u wilt bijdragen.
            </h2>
            <p className="text-[17px] leading-[1.75] text-inkMuted text-pretty">
              We nemen binnen enkele werkdagen contact met u op om de mogelijkheden te bespreken. Al uw gegevens worden
              vertrouwelijk behandeld.
            </p>
            <p className="mt-6 text-[14px] italic text-inkMuted/80">
              Voorkeur voor direct contact? Mail <a href="mailto:info@stichting-fin.nl" className="underline text-petrol hover:text-gold">info@stichting-fin.nl</a>{" "}
              of bel <a href="tel:+31643828430" className="underline text-petrol hover:text-gold">+31 6 43 82 84 30</a>.
            </p>
          </div>
          <div className="lg:col-span-7">
            <form
              onSubmit={onSubmit}
              className="bg-ivory border border-line rounded-3xl p-6 md:p-8 shadow-soft space-y-4"
              data-testid="steun-form"
            >
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Naam" name="naam" value={form.naam} onChange={onChange} required testId="steun-naam" />
                <Field label="E-mail" type="email" name="email" value={form.email} onChange={onChange} required testId="steun-email" />
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Telefoon (optioneel)" name="telefoon" value={form.telefoon} onChange={onChange} testId="steun-telefoon" />
                <Field label="Organisatie (optioneel)" name="organisatie" value={form.organisatie} onChange={onChange} testId="steun-organisatie" />
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-[0.18em] font-bold text-inkMuted block mb-1.5">
                  Waar heeft u interesse in?
                </label>
                <select
                  name="interesse"
                  value={form.interesse}
                  onChange={onChange}
                  className="w-full bg-cream border border-line rounded-xl px-4 py-3 text-sm focus:border-gold focus:outline-none transition"
                  data-testid="steun-interesse"
                >
                  <option value="">Kies een optie…</option>
                  <option value="doneren">Doneren</option>
                  <option value="bedrijfspartner">Bedrijfspartner worden</option>
                  <option value="project-financieren">Project financieren</option>
                  <option value="fonds-sponsor">Als fonds/sponsor bijdragen</option>
                  <option value="expertise">Expertise inbrengen</option>
                  <option value="anders">Anders — vertel het hieronder</option>
                </select>
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-[0.18em] font-bold text-inkMuted block mb-1.5">Bericht *</label>
                <textarea
                  name="bericht"
                  value={form.bericht}
                  onChange={onChange}
                  required
                  rows={6}
                  className="w-full bg-cream border border-line rounded-xl px-4 py-3 text-sm focus:border-gold focus:outline-none transition"
                  placeholder="Waar denkt u aan? Vertel gerust in eigen woorden…"
                  data-testid="steun-bericht"
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                className="w-full inline-flex items-center justify-center gap-2 bg-gold text-petrol hover:bg-goldSoft disabled:opacity-60 disabled:cursor-not-allowed transition rounded-full px-6 py-3.5 text-sm font-semibold shadow-gold"
                data-testid="steun-submit"
              >
                {loading ? "Versturen…" : "Verstuur bericht"} <ArrowUpRight size={16} />
              </button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}

function Field({ label, name, value, onChange, type = "text", required = false, testId }) {
  return (
    <div>
      <label className="text-[11px] uppercase tracking-[0.18em] font-bold text-inkMuted block mb-1.5">
        {label}{required && " *"}
      </label>
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        required={required}
        className="w-full bg-cream border border-line rounded-xl px-4 py-3 text-sm focus:border-gold focus:outline-none transition"
        data-testid={testId}
      />
    </div>
  );
}
